import React, { useCallback, useEffect, useState } from 'react';
import {
  FiDownload, FiTrendingUp, FiTrendingDown, FiUsers,
  FiBarChart2, FiDollarSign, FiActivity,
} from 'react-icons/fi';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import { useToast } from '../context/ToastContext';
import p from './Pages.module.css';

const RANGES = [
  { value: 'today', label: 'Today' },
  { value: '7d',    label: '7 days' },
  { value: '30d',   label: '30 days' },
  { value: 'custom', label: 'Custom' },
];

function fmt(n, decimals = 2) {
  if (n == null) return '—';
  return Number(n).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// ── Stat card ──────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, accent, warn }) {
  const color = accent
    ? 'var(--accent)'
    : warn
    ? '#e0a86c'
    : 'var(--text-primary)';
  return (
    <div className={p.card} style={{ padding: '18px 20px', marginBottom: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ color: 'var(--text-muted)', fontSize: '1rem' }}>{icon}</span>
        <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', fontWeight: 600 }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: '1.6rem', fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

// ── Small table ─────────────────────────────────────────────────────────────
function Table({ columns, rows, empty = 'No data.' }) {
  if (!rows || rows.length === 0) {
    return <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', padding: '20px 0' }}>{empty}</div>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c.key} style={{
                textAlign: c.right ? 'right' : 'left',
                padding: '6px 10px',
                color: 'var(--text-muted)',
                fontWeight: 600,
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                borderBottom: '1px solid var(--border-color)',
                whiteSpace: 'nowrap',
              }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {columns.map(c => (
                <td key={c.key} style={{
                  padding: '8px 10px',
                  textAlign: c.right ? 'right' : 'left',
                  color: c.render ? undefined : 'var(--text-secondary)',
                  whiteSpace: 'nowrap',
                }}>
                  {c.render ? c.render(row[c.key], row) : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Outcome pill ─────────────────────────────────────────────────────────────
function OutcomePill({ label, count, bg, color }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 12px', borderRadius: 6,
      background: bg, color, fontSize: '0.8rem', fontWeight: 600,
    }}>
      {label} <span style={{ opacity: 0.85 }}>{count}</span>
    </span>
  );
}

// Defined outside the component so the array reference is stable across renders.
const PNL_COLUMNS = [
  {
    key: 'externalUserId', label: 'User',
    render: (v, row) => (
      <div>
        {row.displayName && (
          <div style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.82rem' }}>
            {row.displayName}
          </div>
        )}
        <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>{v}</div>
      </div>
    ),
  },
  { key: 'integrationCode', label: 'Integration' },
  { key: 'betCount', label: 'Bets', right: true },
  { key: 'totalStaked', label: 'Staked', right: true, render: v => `$${fmt(v)}` },
  {
    key: 'pnl', label: 'P&L', right: true,
    render: v => (
      <span style={{ color: v >= 0 ? 'var(--accent)' : '#e06c75', fontWeight: 600 }}>
        {v >= 0 ? '+' : ''}${fmt(v)}
      </span>
    ),
  },
];

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Analytics() {
  const api = useApi();
  const { token } = useAuth();
  const toast = useToast();

  const [range, setRange] = useState('7d');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [integrationId, setIntegrationId] = useState('');
  const [integrations, setIntegrations] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pnlTab, setPnlTab] = useState('won'); // 'won' | 'lost'

  // Load integrations for the filter dropdown
  useEffect(() => {
    api.get('/v1/clasico-integrations/').then(r => {
      setIntegrations(Array.isArray(r) ? r : (r?.items || []));
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (range !== 'custom') {
      params.set('range', range);
    } else {
      if (fromDate) params.set('from', fromDate);
      if (toDate)   params.set('to', toDate);
    }
    if (integrationId) params.set('integrationId', integrationId);
    return params.toString();
  }, [range, fromDate, toDate, integrationId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.get(`/v1/analytics/manual-markets?${buildQuery()}`);
      setData(result);
    } catch (e) {
      toast.error(`Failed to load analytics: ${e.message}`);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildQuery]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const qs = buildQuery();
      const res = await fetch(
        `${API_BASE_URL}/v1/analytics/manual-markets?${qs}&format=csv`,
        { headers: { 'auth-token': token || '' } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'manual-market-bets.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`CSV export failed: ${e.message}`);
    }
  };

  const s = data?.summary;

  return (
    <div className={p.page}>
      <div className={p.pageHeader}>
        <h1 className={p.pageTitle}>Analytics</h1>
        <p className={p.pageSubtitle}>Manual market betting activity — admin view</p>
      </div>

      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 24 }}>
        {/* Range pills */}
        <div style={{ display: 'flex', gap: 4 }}>
          {RANGES.map(r => (
            <button
              key={r.value}
              className={range === r.value ? p.btnPrimary : p.btnOutline}
              style={{ padding: '6px 14px', fontSize: '0.82rem' }}
              onClick={() => setRange(r.value)}
            >
              {r.label}
            </button>
          ))}
        </div>

        {range === 'custom' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="date" className={p.formInput} value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              style={{ width: 148, padding: '6px 10px' }}
            />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.82rem' }}>to</span>
            <input
              type="date" className={p.formInput} value={toDate}
              onChange={e => setToDate(e.target.value)}
              style={{ width: 148, padding: '6px 10px' }}
            />
          </div>
        )}

        <select
          className={p.formSelect}
          value={integrationId}
          onChange={e => setIntegrationId(e.target.value)}
          style={{ width: 180, padding: '6px 10px', fontSize: '0.82rem' }}
        >
          <option value="">All integrations</option>
          {integrations.map(i => (
            <option key={i.id} value={i.id}>{i.name || i.code || `#${i.id}`}</option>
          ))}
        </select>

        <button className={p.btnOutline} onClick={downloadCsv} style={{ marginLeft: 'auto' }}>
          <FiDownload /> Export CSV
        </button>
      </div>

      {loading && <div style={{ color: 'var(--text-muted)', marginBottom: 20 }}>Loading…</div>}

      {data && (
        <>
          {/* ── Summary cards ── */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 14,
            marginBottom: 16,
          }}>
            <StatCard
              icon={<FiActivity />}
              label="Total bets"
              value={s.betCount.toLocaleString()}
              sub={`Won ${s.wonCount} · Lost ${s.lostCount} · Pending ${s.pendingCount}`}
            />
            <StatCard
              icon={<FiDollarSign />}
              label="Total staked"
              value={`$${fmt(s.totalStaked)}`}
            />
            <StatCard
              icon={<FiDollarSign />}
              label="Total payout"
              value={`$${fmt(s.totalPayout)}`}
            />
            <StatCard
              icon={<FiBarChart2 />}
              label="Operator margin"
              value={`$${fmt(s.operatorMargin)}`}
              sub={`${fmt(s.operatorMarginPct, 1)}% of stake`}
              accent={s.operatorMargin >= 0}
              warn={s.operatorMargin < 0}
            />
            <StatCard
              icon={<FiUsers />}
              label="Unique players"
              value={s.userCount.toLocaleString()}
            />
            <StatCard
              icon={<FiBarChart2 />}
              label="Markets"
              value={s.marketCount.toLocaleString()}
            />
          </div>

          {/* ── Outcome pills ── */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
            <OutcomePill label="Won" count={s.wonCount} bg="rgba(115,201,145,0.15)" color="var(--accent)" />
            <OutcomePill label="Lost" count={s.lostCount} bg="rgba(224,108,117,0.12)" color="#e06c75" />
            <OutcomePill label="Pending" count={s.pendingCount} bg="rgba(224,168,108,0.12)" color="#e0a86c" />
          </div>

          {/* ── Top spenders / P&L ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, marginBottom: 18 }}>

            {/* Top spenders */}
            <div className={p.card} style={{ marginBottom: 0 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Top spenders
              </h3>
              <Table
                columns={[
                  {
                    key: 'externalUserId', label: 'User',
                    render: (v, row) => (
                      <div>
                        {row.displayName && (
                          <div style={{ color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.82rem' }}>
                            {row.displayName}
                          </div>
                        )}
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>{v}</div>
                      </div>
                    ),
                  },
                  { key: 'integrationCode', label: 'Integration' },
                  { key: 'betCount', label: 'Bets', right: true },
                  {
                    key: 'totalStaked', label: 'Staked', right: true,
                    render: v => <span style={{ color: 'var(--text-primary)' }}>${fmt(v)}</span>,
                  },
                  {
                    key: 'pnl', label: 'P&L', right: true,
                    render: v => (
                      <span style={{ color: v >= 0 ? 'var(--accent)' : '#e06c75', fontWeight: 600 }}>
                        {v >= 0 ? '+' : ''}${fmt(v)}
                      </span>
                    ),
                  },
                ]}
                rows={data.topSpenders}
                empty="No bets in this period."
              />
            </div>

            {/* Most won / most lost */}
            <div className={p.card} style={{ marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
                <button
                  type="button"
                  onClick={() => setPnlTab('won')}
                  style={{
                    padding: '4px 12px', fontSize: '0.82rem', fontWeight: 600, borderRadius: '6px 0 0 6px',
                    border: '1px solid var(--border-color)', cursor: 'pointer',
                    background: pnlTab === 'won' ? 'rgba(115,201,145,0.15)' : 'transparent',
                    color: pnlTab === 'won' ? 'var(--accent)' : 'var(--text-muted)',
                    borderRight: 'none',
                  }}
                >
                  <FiTrendingUp style={{ verticalAlign: 'middle', marginRight: 4 }} />Most won
                </button>
                <button
                  type="button"
                  onClick={() => setPnlTab('lost')}
                  style={{
                    padding: '4px 12px', fontSize: '0.82rem', fontWeight: 600, borderRadius: '0 6px 6px 0',
                    border: '1px solid var(--border-color)', cursor: 'pointer',
                    background: pnlTab === 'lost' ? 'rgba(224,108,117,0.12)' : 'transparent',
                    color: pnlTab === 'lost' ? '#e06c75' : 'var(--text-muted)',
                  }}
                >
                  <FiTrendingDown style={{ verticalAlign: 'middle', marginRight: 4 }} />Most lost
                </button>
              </div>
              {pnlTab === 'won' ? (
                <Table
                  key="won"
                  columns={PNL_COLUMNS}
                  rows={data.mostWon}
                  empty="No settled bets in this period."
                />
              ) : (
                <Table
                  key="lost"
                  columns={PNL_COLUMNS}
                  rows={data.mostLost}
                  empty="No settled bets in this period."
                />
              )}
            </div>
          </div>

          {/* ── Per-market table ── */}
          <div className={p.card} style={{ marginBottom: 18 }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              Per-market breakdown
            </h3>
            <Table
              columns={[
                { key: 'freeText', label: 'Market' },
                {
                  key: 'status', label: 'Status',
                  render: v => (
                    <span style={{
                      padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600,
                      background: v === 'settled' ? 'rgba(115,201,145,0.15)'
                        : v === 'void' ? 'rgba(224,168,108,0.12)'
                        : 'rgba(108,140,191,0.12)',
                      color: v === 'settled' ? 'var(--accent)'
                        : v === 'void' ? '#e0a86c'
                        : 'var(--text-secondary)',
                    }}>
                      {v}
                    </span>
                  ),
                },
                { key: 'betCount', label: 'Bets', right: true },
                { key: 'totalStaked', label: 'Staked', right: true, render: v => `$${fmt(v)}` },
                { key: 'totalPayout', label: 'Payout', right: true, render: v => `$${fmt(v)}` },
                {
                  key: 'operatorMargin', label: 'Margin', right: true,
                  render: (v, row) => (
                    <span style={{ color: v >= 0 ? 'var(--accent)' : '#e06c75', fontWeight: 600 }}>
                      ${fmt(v)} <span style={{ opacity: 0.65, fontWeight: 400 }}>({fmt(row.operatorMarginPct, 1)}%)</span>
                    </span>
                  ),
                },
                {
                  key: 'winRatePct', label: 'Win rate', right: true,
                  render: v => `${fmt(v, 1)}%`,
                },
              ]}
              rows={data.perMarket}
              empty="No bets in this period."
            />
          </div>

          {/* ── Per-integration table (only when > 1) ── */}
          {data.perIntegration.length > 0 && (
            <div className={p.card} style={{ marginBottom: 0 }}>
              <h3 style={{ margin: '0 0 14px', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                Per-integration breakdown
              </h3>
              <Table
                columns={[
                  { key: 'integrationCode', label: 'Integration' },
                  { key: 'integrationName', label: 'Name' },
                  { key: 'betCount', label: 'Bets', right: true },
                  { key: 'userCount', label: 'Players', right: true },
                  { key: 'totalStaked', label: 'Staked', right: true, render: v => `$${fmt(v)}` },
                  { key: 'totalPayout', label: 'Payout', right: true, render: v => `$${fmt(v)}` },
                  {
                    key: 'operatorMargin', label: 'Margin', right: true,
                    render: (v, row) => (
                      <span style={{ color: v >= 0 ? 'var(--accent)' : '#e06c75', fontWeight: 600 }}>
                        ${fmt(v)} <span style={{ opacity: 0.65, fontWeight: 400 }}>({fmt(row.operatorMarginPct, 1)}%)</span>
                      </span>
                    ),
                  },
                ]}
                rows={data.perIntegration}
              />
            </div>
          )}
        </>
      )}

      {!loading && !data && (
        <div className={p.emptyState}>
          <div className={p.emptyText}>No data loaded.</div>
        </div>
      )}
    </div>
  );
}
